import React, { useEffect, useMemo, useRef, useState } from "react";
import type { InterviewSlots } from "../../../Models";
import ConfirmModal from "../../Shared/ConfirmModal";
import { Spinner } from "../../Shared/Spinner";
import {
  ChatBubbleBottomCenterTextIcon,
  TrashIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid";
import {
  CalendarDaysIcon,
  XMarkIcon,
  ListBulletIcon,
  Squares2X2Icon,
  ArrowDownTrayIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  UserIcon,
  UserCircleIcon,
  VideoCameraIcon,
  FunnelIcon,
  NoSymbolIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import ReactDatePicker from "react-datepicker";
import useApiToken from "../../../hooks/useApiToken";
import useGraphApiToken from "../../../hooks/useGraphApiToken";
import useClickOutside from "../../../hooks/useClickOutside";
import { useNotification } from "../../../context/NotificationContext";
import { SearchUsers, GraphUser } from "../../../api/microsoft.api";
import {
  DeleteInterviewSlot,
  DeleteInterviewSlots,
  GetInterviewSlots,
  updateInterviewSlot,
  UnbookInterviewSlots,
} from "../../../api/roles.api";

type InterviewSlotslProps = {
  isOpen: boolean;
  onClose: () => void;
  roleId: string | undefined;
};
const InterviewSlotShow = ({
  isOpen,
  onClose,
  roleId,
}: InterviewSlotslProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [slots, setSlots] = useState<InterviewSlots[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<InterviewSlots | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [filterStageId, setFilterStageId] = useState<string>("");
  const [filterInterviewer, setFilterInterviewer] = useState<string>("");
  const [filterFrom, setFilterFrom] = useState<Date | null>(null);
  const [filterTo, setFilterTo] = useState<Date | null>(null);
  const [sortBy, setSortBy] = useState<"time" | "interviewer" | "interviewee">("time");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [interviewerNameMap, setInterviewerNameMap] = useState<Record<string, string>>({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [meetingLink, setMeetingLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [interviewerEmail, setInterviewerEmail] = useState<string | null>(null);
  const [selectedInterviewers, setSelectedInterviewers] = useState<{ id: string; displayName: string; email: string }[]>([]);
  const [interviewerSearch, setInterviewerSearch] = useState("");
  const [searchResults, setSearchResults] = useState<GraphUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(new Set());
  const [slotsToUnbook, setSlotsToUnbook] = useState<InterviewSlots[]>([]);
  const [showUnbookModal, setShowUnbookModal] = useState(false);
  const [slotsToDelete, setSlotsToDelete] = useState<InterviewSlots[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const token = useApiToken();
  const graphToken = useGraphApiToken();
  const editSlotRef = useRef(null);
  const { success, error: notifyError } = useNotification();
  useEffect(() => {
    if (!token) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setFilterFrom(today);
    fetchSlots();
  }, [token]);

  const stageOptions = useMemo(() => {
    const map = new Map<string, string>();
    slots.forEach((s) => map.set(s.roleStatusId, s.roleStatus?.name || s.roleStatusId));
    return Array.from(map.entries());
  }, [slots]);

  const interviewerOptions = useMemo(() => {
    const set = new Set<string>();
    slots.forEach((s) => {
      (s.interviewerEmail || "")
        .split(",")
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
        .forEach((e) => set.add(e));
    });
    return Array.from(set).sort();
  }, [slots]);

  // Resolve interviewer emails to display names via Graph search (falls back to the raw email)
  useEffect(() => {
    if (!graphToken || interviewerOptions.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        interviewerOptions.map(async (email) => {
          try {
            const results = await SearchUsers(graphToken, email);
            const match =
              results.find(
                (r) => (r.mail || r.userPrincipalName || "").toLowerCase() === email
              ) || results[0];
            return [email, match?.displayName || email] as const;
          } catch {
            return [email, email] as const;
          }
        })
      );
      if (!cancelled) {
        setInterviewerNameMap((prev) => {
          const next = { ...prev };
          entries.forEach(([email, name]) => {
            next[email] = name;
          });
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [interviewerOptions, graphToken]);

  const resolveInterviewerNames = (emailStr: string | null): string => {
    if (!emailStr) return "—";
    return emailStr
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean)
      .map((e) => interviewerNameMap[e.toLowerCase()] || e)
      .join(", ");
  };

  const filteredSlots = useMemo(() => {
    return slots.filter((slot) => {
      if (filterFrom && new Date(slot.startTime) < filterFrom) return false;
      if (filterTo && new Date(slot.startTime) > filterTo) return false;
      if (filterStageId && slot.roleStatusId !== filterStageId) return false;
      if (filterInterviewer) {
        const emails = (slot.interviewerEmail || "")
          .split(",")
          .map((e) => e.trim().toLowerCase());
        if (!emails.includes(filterInterviewer)) return false;
      }
      return true;
    });
  }, [slots, filterFrom, filterTo, filterStageId, filterInterviewer]);

  const sortedSlots = useMemo(() => {
    const arr = [...filteredSlots];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let cmp = 0;
      if (sortBy === "time") {
        cmp = new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      } else if (sortBy === "interviewer") {
        cmp = resolveInterviewerNames(a.interviewerEmail).localeCompare(
          resolveInterviewerNames(b.interviewerEmail)
        );
      } else if (sortBy === "interviewee") {
        const an = a.candidate ? `${a.candidate.firstName} ${a.candidate.lastName}` : "";
        const bn = b.candidate ? `${b.candidate.firstName} ${b.candidate.lastName}` : "";
        cmp = an.localeCompare(bn);
      }
      return cmp * dir;
    });
    return arr;
  }, [filteredSlots, sortBy, sortDir, interviewerNameMap]);

  const selectedBookedSlots = useMemo(
    () => sortedSlots.filter((s) => selectedSlotIds.has(s.id) && !!s.candidateId),
    [sortedSlots, selectedSlotIds]
  );
  const selectedUnbookedSlots = useMemo(
    () => sortedSlots.filter((s) => selectedSlotIds.has(s.id) && !s.candidateId),
    [sortedSlots, selectedSlotIds]
  );
  const isAllVisibleSelected =
    sortedSlots.length > 0 && sortedSlots.every((s) => selectedSlotIds.has(s.id));

  const toggleSelectSlot = (id: string) => {
    setSelectedSlotIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedSlotIds((prev) => {
      const next = new Set(prev);
      if (isAllVisibleSelected) {
        sortedSlots.forEach((s) => next.delete(s.id));
      } else {
        sortedSlots.forEach((s) => next.add(s.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedSlotIds(new Set());

  const toggleSort = (field: "time" | "interviewer" | "interviewee") => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const exportRowsToCSV = (rows: Record<string, string>[], filename: string) => {
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (val: string) => {
      const str = String(val ?? "");
      if (str.includes(",") || str.includes('"') || str.includes("\n")) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const csvLines = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
    ];
    const csv = "﻿" + csvLines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    const rows = sortedSlots.map((slot) => ({
      Date: new Date(slot.startTime).toLocaleDateString(),
      "Start Time": new Date(slot.startTime).toLocaleTimeString(),
      "End Time": new Date(slot.endTime).toLocaleTimeString(),
      Interviewer: resolveInterviewerNames(slot.interviewerEmail),
      Interviewee: slot.candidate ? `${slot.candidate.firstName} ${slot.candidate.lastName}` : "",
      Stage: slot.roleStatus?.name || "",
      "Meeting Link": slot.meetingLink || "",
    }));
    exportRowsToCSV(rows, `interview-slots-${roleId || "role"}.csv`);
  };
  useEffect(() => {
    if (selectedSlot) {
      setStartTime(new Date(selectedSlot.startTime));
      setEndTime(new Date(selectedSlot.endTime));
      setMeetingLink(selectedSlot.meetingLink || "");
      setInterviewerEmail(selectedSlot.interviewerEmail);
      // Pre-populate chips from the saved email string (no name lookup needed)
      if (selectedSlot.interviewerEmail) {
        const emails = selectedSlot.interviewerEmail.split(',').map((e: string) => e.trim()).filter(Boolean);
        setSelectedInterviewers(
          emails.map((email: string) => ({ id: email, displayName: email, email }))
        );
      } else {
        setSelectedInterviewers([]);
      }
    }
  }, [selectedSlot]);

  // Debounced Graph API search — only fires when typing
  useEffect(() => {
    if (interviewerSearch.length < 2) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      if (!graphToken) return;
      setIsSearching(true);
      try {
        const results = await SearchUsers(graphToken, interviewerSearch);
        setSearchResults(results);
        setShowDropdown(results.length > 0);
      } catch {
        setSearchResults([]);
        setShowDropdown(false);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [interviewerSearch, graphToken]);

  const addInterviewer = (user: GraphUser) => {
    const email = user.mail || user.userPrincipalName;
    if (!email) return;
    if (selectedInterviewers.some((p) => p.id === user.id)) return;
    const updated = [...selectedInterviewers, { id: user.id, displayName: user.displayName, email }];
    setSelectedInterviewers(updated);
    setInterviewerEmail(updated.map((p) => p.email).join(', '));
    setInterviewerSearch("");
    setShowDropdown(false);
    setSearchResults([]);
  };

  const removeInterviewer = (id: string) => {
    const updated = selectedInterviewers.filter((p) => p.id !== id);
    setSelectedInterviewers(updated);
    setInterviewerEmail(updated.length > 0 ? updated.map((p) => p.email).join(', ') : null);
  };
  const handleEditSubmit = async () => {
    if (!selectedSlot) return;

    if (validateInputs() && token) {
      try {
        await updateInterviewSlot({
          slotId: selectedSlot.id,
          startTime: startTime?.toISOString() || "",
          endTime: endTime?.toISOString() || "",
          meetingLink: meetingLink || "",
          interviewerEmail: interviewerEmail,
          notes: "",
        }, token);
        handleCloseEditModal();
        await fetchSlots();
      } catch (error: any) {
        setError(error.message);
      }
    }
  };
  const validateInputs = (): boolean => {
    if (!startTime || !endTime || !meetingLink?.trim()) {
      setError("All fields are required.");
      return false;
    }
    if (endTime <= startTime) {
      setError("End time must be after start time.");
      return false;
    }
    setError(null);
    return true;
  };
  const fetchSlots = async () => {
    setIsLoading(true);
    try {
      if (!token) return;
      const slotsData = await GetInterviewSlots(roleId || "", token);
      setSlots(slotsData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching slots:", error);
      setIsLoading(false);
    }
  };

  const handleDeleteSlot = (slot: InterviewSlots) => {
    setSelectedSlot(slot);
    setShowModal(true);
  };
  const handleEditSlot = (slot: InterviewSlots) => {
    setSelectedSlot(slot);
    setShowEditModal(true);
  };
  const handleDeleteConfirm = async (slotId: string) => {
    try {
      if (!token) return;
      await DeleteInterviewSlot(slotId, token);
      setSelectedSlot(null);
      setShowModal(false);
      success("Deleted Successfully", "The interview slot has been deleted.");
      await fetchSlots();
    } catch (err: any) {
      setError(err.message || "Failed to delete slot.");
      notifyError("Deletion Failed", err.message || "An error occurred while deleting the slot.");
    }
  };
  const handleUnbookSlotClick = (slot: InterviewSlots) => {
    setSlotsToUnbook([slot]);
    setShowUnbookModal(true);
  };
  const handleBulkUnbookClick = () => {
    if (selectedBookedSlots.length === 0) return;
    setSlotsToUnbook(selectedBookedSlots);
    setShowUnbookModal(true);
  };
  const handleUnbookConfirm = async () => {
    if (!token || slotsToUnbook.length === 0) return;
    try {
      const { results } = await UnbookInterviewSlots(slotsToUnbook.map((s) => s.id), token);
      const successCount = results.filter((r: any) => r.success).length;
      const failCount = results.length - successCount;
      setShowUnbookModal(false);
      setSelectedSlotIds((prev) => {
        const next = new Set(prev);
        slotsToUnbook.forEach((s) => next.delete(s.id));
        return next;
      });
      setSlotsToUnbook([]);
      await fetchSlots();
      if (failCount === 0) {
        success(
          "Unbooked Successfully",
          `${successCount} slot${successCount === 1 ? "" : "s"} unbooked. The candidate${successCount === 1 ? "" : "s"} will be notified by email.`
        );
      } else {
        notifyError(
          "Partially Completed",
          `${successCount} slot${successCount === 1 ? "" : "s"} unbooked, ${failCount} failed.`
        );
      }
    } catch (err: any) {
      notifyError("Unbook Failed", err.message || "An error occurred while unbooking the selected slots.");
    }
  };
  const handleBulkDeleteClick = () => {
    if (selectedUnbookedSlots.length === 0) return;
    setSlotsToDelete(selectedUnbookedSlots);
    setShowBulkDeleteModal(true);
  };
  const handleBulkDeleteConfirm = async () => {
    if (!token || slotsToDelete.length === 0) return;
    try {
      const { results } = await DeleteInterviewSlots(slotsToDelete.map((s) => s.id), token);
      const successCount = results.filter((r: any) => r.success).length;
      const failCount = results.length - successCount;
      setShowBulkDeleteModal(false);
      setSelectedSlotIds((prev) => {
        const next = new Set(prev);
        slotsToDelete.forEach((s) => next.delete(s.id));
        return next;
      });
      setSlotsToDelete([]);
      await fetchSlots();
      if (failCount === 0) {
        success(
          "Deleted Successfully",
          `${successCount} slot${successCount === 1 ? "" : "s"} deleted.`
        );
      } else {
        notifyError(
          "Partially Completed",
          `${successCount} slot${successCount === 1 ? "" : "s"} deleted, ${failCount} failed.`
        );
      }
    } catch (err: any) {
      notifyError("Deletion Failed", err.message || "An error occurred while deleting the selected slots.");
    }
  };
  const handleClearPastSlots = async () => {
    // Only target past slots that are NOT booked (no candidateId)
    const pastUnbookedSlots = slots.filter(slot => 
      new Date(slot.endTime) < new Date() && !slot.candidateId
    );

    if (pastUnbookedSlots.length === 0) {
      const bookedPastCount = slots.filter(slot => 
        new Date(slot.endTime) < new Date() && slot.candidateId
      ).length;
      
      if (bookedPastCount > 0) {
        alert(`No unbooked past slots found. Total of ${bookedPastCount} past slots are booked with candidates and cannot be bulk deleted.`);
      } else {
        alert("No past slots found to delete.");
      }
      return;
    }

    if (window.confirm(`Are you sure you want to delete all ${pastUnbookedSlots.length} unbooked past slots? Booked slots will be preserved.`)) {
      try {
        setIsLoading(true);
        if (!token) return;

        const { results } = await DeleteInterviewSlots(pastUnbookedSlots.map((s) => s.id), token);
        const deletedCount = results.filter((r: any) => r.success).length;
        const failCount = results.length - deletedCount;

        await fetchSlots();
        if (failCount === 0) {
          alert(`Successfully deleted all ${deletedCount} unbooked past slots.`);
        } else {
          alert(`Deleted ${deletedCount} slots, but ${failCount} slots failed to delete.`);
        }
      } catch (error: any) {
        notifyError("Error", "An unexpected error occurred during bulk deletion.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setStartTime(null);
    setEndTime(null);
    setMeetingLink(null);
    setInterviewerEmail(null);
    setSelectedInterviewers([]);
    setInterviewerSearch("");
    setSearchResults([]);
    setShowDropdown(false);
    setError(null);
    setSelectedSlot(null);
  };
  useClickOutside(editSlotRef, () => setShowEditModal(false));
  if (!isOpen) return null;

  const inputCls =
    "px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#0d42ec]/30 focus:border-[#0d42ec] transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500";
  const labelCls = "block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5";
  const stageBadgeCls =
    "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#0d42ec]/10 text-[#0d42ec] dark:bg-[#0d42ec]/20 whitespace-nowrap";
  const iconBtnCls =
    "p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors";

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50 p-4">
      <div className="relative bg-white dark:bg-gray-950 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-[95%] max-h-[88vh] overflow-y-auto">
        <div className="sticky top-0 z-20 bg-white dark:bg-gray-950 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-[#0d42ec] rounded-lg">
                <CalendarDaysIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                  Interview Slots
                </h2>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {sortedSlots.length} of {slots.length} slot{slots.length === 1 ? "" : "s"} shown
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          <div className="mt-4 p-3.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className={labelCls}>From</label>
                <ReactDatePicker
                  selected={filterFrom}
                  onChange={(date: Date | null) => setFilterFrom(date)}
                  showTimeSelect
                  dateFormat="MMM d, yyyy h:mm aa"
                  isClearable
                  placeholderText="No lower bound"
                  popperProps={{ strategy: "fixed" }}
                  popperClassName="!z-[9999]"
                  className={`${inputCls} w-44`}
                />
              </div>
              <div>
                <label className={labelCls}>To</label>
                <ReactDatePicker
                  selected={filterTo}
                  onChange={(date: Date | null) => setFilterTo(date)}
                  showTimeSelect
                  dateFormat="MMM d, yyyy h:mm aa"
                  isClearable
                  placeholderText="No upper bound"
                  popperProps={{ strategy: "fixed" }}
                  popperClassName="!z-[9999]"
                  className={`${inputCls} w-44`}
                />
              </div>
              <div>
                <label className={labelCls}>Stage</label>
                <select
                  value={filterStageId}
                  onChange={(e) => setFilterStageId(e.target.value)}
                  className={`${inputCls} cursor-pointer`}
                >
                  <option value="">All Stages</option>
                  {stageOptions.map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Interviewer</label>
                <select
                  value={filterInterviewer}
                  onChange={(e) => setFilterInterviewer(e.target.value)}
                  className={`${inputCls} cursor-pointer max-w-[12rem]`}
                >
                  <option value="">All Interviewers</option>
                  {interviewerOptions.map((email) => (
                    <option key={email} value={email}>
                      {interviewerNameMap[email] || email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 ml-auto">
                <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    title="List view"
                    className={`p-2 transition-colors ${viewMode === "list" ? "bg-[#0d42ec] text-white" : "bg-white dark:bg-gray-900 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"}`}
                  >
                    <ListBulletIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("grid")}
                    title="Grid view"
                    className={`p-2 transition-colors ${viewMode === "grid" ? "bg-[#0d42ec] text-white" : "bg-white dark:bg-gray-900 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"}`}
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                  </button>
                </div>
                <button
                  onClick={handleExport}
                  disabled={sortedSlots.length === 0}
                  className="px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-[#0d42ec]/40 hover:text-[#0d42ec] text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Export the currently filtered & sorted slots"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Export
                </button>
                <button
                  onClick={handleClearPastSlots}
                  className="px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-red-300 hover:text-red-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                  title="Delete all slots that have already ended"
                >
                  <TrashIcon className="h-4 w-4" />
                  Clear Past
                </button>
              </div>
            </div>

            {viewMode === "list" && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800 flex items-center gap-4 text-xs font-semibold text-gray-500 dark:text-gray-400">
                <span>Sort by:</span>
                {(["time", "interviewer", "interviewee"] as const).map((field) => (
                  <button
                    key={field}
                    type="button"
                    onClick={() => toggleSort(field)}
                    className={`flex items-center gap-1 transition-colors hover:text-[#0d42ec] ${sortBy === field ? "text-[#0d42ec]" : ""}`}
                  >
                    <span className="capitalize">{field}</span>
                    {sortBy === field && (sortDir === "asc" ? (
                      <ArrowUpIcon className="h-3 w-3" />
                    ) : (
                      <ArrowDownIcon className="h-3 w-3" />
                    ))}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedSlotIds.size > 0 && (
            <div className="mt-3 p-3 bg-[#0d42ec]/5 border border-[#0d42ec]/20 rounded-lg flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-[#0d42ec]">
                {selectedSlotIds.size} selected
              </span>
              <span className="text-xs text-gray-400">
                ({selectedBookedSlots.length} booked)
              </span>
              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={handleBulkUnbookClick}
                  disabled={selectedBookedSlots.length === 0}
                  className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Unbook the selected booked slots and notify their candidates by email"
                >
                  <NoSymbolIcon className="h-4 w-4" />
                  Cancel/Unbook Selected ({selectedBookedSlots.length})
                </button>
                <button
                  type="button"
                  onClick={handleBulkDeleteClick}
                  disabled={selectedUnbookedSlots.length === 0}
                  className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-red-200 dark:border-red-900 hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Delete the selected unbooked slots"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete Selected ({selectedUnbookedSlots.length})
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:border-gray-300 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col items-center mt-4 px-6 pb-6 w-full">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center">
              <Spinner height="h-10" width="10" />
            </div>
          ) : sortedSlots.length === 0 ? (
            <div className="flex flex-col items-center py-16 gap-3">
              <div className="p-3 bg-gray-100 dark:bg-gray-900 rounded-full">
                <CalendarDaysIcon className="h-7 w-7 text-gray-400" />
              </div>
              <p className="text-sm text-gray-400">No interview slots match the current filters.</p>
            </div>
          ) : viewMode === "list" ? (
            <div className="w-full rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                    <th className="py-2.5 px-3 w-8">
                      <input
                        type="checkbox"
                        checked={isAllVisibleSelected}
                        onChange={toggleSelectAllVisible}
                        className="cursor-pointer accent-[#0d42ec]"
                        aria-label="Select all visible slots"
                      />
                    </th>
                    <th className="py-2.5 px-3">Date</th>
                    <th className="py-2.5 px-3">Time</th>
                    <th className="py-2.5 px-3">Interviewer(s)</th>
                    <th className="py-2.5 px-3">Interviewee</th>
                    <th className="py-2.5 px-3">Stage</th>
                    <th className="py-2.5 px-3">Meeting</th>
                    <th className="py-2.5 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSlots.map((slot) => (
                    <tr
                      key={slot.id}
                      className="border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-[#0d42ec]/5 dark:hover:bg-[#0d42ec]/10 transition-colors"
                    >
                      <td className="py-2.5 px-3">
                        <input
                          type="checkbox"
                          checked={selectedSlotIds.has(slot.id)}
                          onChange={() => toggleSelectSlot(slot.id)}
                          className="cursor-pointer accent-[#0d42ec]"
                          aria-label="Select slot"
                        />
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap text-gray-700 dark:text-gray-300 font-medium">
                        {new Date(slot.startTime).toLocaleDateString(undefined, {
                          weekday: "short",
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                      <td className="py-2.5 px-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {new Date(slot.startTime).toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {" – "}
                        {new Date(slot.endTime).toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300 max-w-[14rem] truncate" title={resolveInterviewerNames(slot.interviewerEmail)}>
                        <span className="inline-flex items-center gap-1.5">
                          <UserIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          {resolveInterviewerNames(slot.interviewerEmail)}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-gray-600 dark:text-gray-300">
                        {slot.candidate ? (
                          <Link
                            to={`/roles/${slot.roleId}/candidates/${slot.candidate.id}`}
                            className="inline-flex items-center gap-1.5 text-[#0d42ec] hover:underline"
                          >
                            <UserCircleIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            {slot.candidate.firstName + " " + slot.candidate.lastName}
                          </Link>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 italic">Not booked</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={stageBadgeCls}>{slot.roleStatus.name}</span>
                      </td>
                      <td className="py-2.5 px-3">
                        {slot.meetingLink ? (
                          <a
                            href={slot.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#0d42ec] hover:underline"
                          >
                            <VideoCameraIcon className="h-3.5 w-3.5" />
                            Join
                          </a>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* The dedicated slot page was removed - notes and scoring now
                              live on the candidate profile, so this points there instead.
                              Nothing to open when the slot isn't booked yet. */}
                          {slot.candidate ? (
                            <Link
                              to={`/roles/${slot.roleId}/candidates/${slot.candidate.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={iconBtnCls}
                            >
                              <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-entntblue" />
                            </Link>
                          ) : (
                            <span className={`${iconBtnCls} opacity-40 cursor-not-allowed`}>
                              <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-gray-400" />
                            </span>
                          )}
                          <button
                            type="button"
                            disabled={!!slot.candidateId}
                            onClick={() => handleEditSlot(slot)}
                            className={`${iconBtnCls} disabled:cursor-not-allowed disabled:opacity-40`}
                          >
                            <PencilSquareIcon className="h-4 w-4 text-entntblue" />
                          </button>
                          <button
                            type="button"
                            disabled={!!slot.candidateId}
                            onClick={() => handleDeleteSlot(slot)}
                            className={`${iconBtnCls} disabled:cursor-not-allowed disabled:opacity-40`}
                          >
                            <TrashIcon className="h-4 w-4 text-red-600" />
                          </button>
                          <button
                            type="button"
                            disabled={!slot.candidateId}
                            onClick={() => handleUnbookSlotClick(slot)}
                            title="Unbook this slot and notify the candidate by email"
                            className={`${iconBtnCls} disabled:cursor-not-allowed disabled:opacity-40`}
                          >
                            <NoSymbolIcon className="h-4 w-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 w-full">
              {sortedSlots.map((slot) => (
                <div
                  key={slot.id}
                  className="relative p-3.5 border border-gray-200 dark:border-gray-800 rounded-xl bg-white dark:bg-gray-900 shadow-sm hover:shadow-md hover:border-[#0d42ec]/40 transition-all flex flex-col gap-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedSlotIds.has(slot.id)}
                    onChange={() => toggleSelectSlot(slot.id)}
                    className="absolute top-3 left-3 cursor-pointer accent-[#0d42ec]"
                    aria-label="Select slot"
                  />
                  <div className="flex items-start justify-between gap-2 pl-5">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {new Date(slot.startTime).toLocaleDateString(undefined, {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                      })}
                    </span>
                    <span className={stageBadgeCls}>{slot.roleStatus.name}</span>
                  </div>

                  <div className="text-sm font-bold text-gray-800 dark:text-gray-100">
                    {new Date(slot.startTime).toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {" – "}
                    {new Date(slot.endTime).toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>

                  <div className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                    <UserIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                    <span className="break-words">{resolveInterviewerNames(slot.interviewerEmail)}</span>
                  </div>

                  <div className="flex items-start gap-1.5 text-xs">
                    <UserCircleIcon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-gray-400" />
                    {slot.candidate ? (
                      <Link
                        to={`/roles/${slot.roleId}/candidates/${slot.candidate.id}`}
                        className="text-[#0d42ec] hover:underline break-words"
                      >
                        {slot.candidate.firstName + " " + slot.candidate.lastName}
                      </Link>
                    ) : (
                      <span className="text-gray-300 dark:text-gray-600 italic">Not booked</span>
                    )}
                  </div>

                  {slot.meetingLink && (
                    <a
                      href={slot.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#0d42ec] hover:underline"
                    >
                      <VideoCameraIcon className="h-3.5 w-3.5" />
                      Join Meeting
                    </a>
                  )}

                  <div className="flex w-full mt-1 pt-2 border-t border-gray-100 dark:border-gray-800 items-center justify-around">
                    {/* The dedicated slot page was removed - notes and scoring now live
                        on the candidate profile, so this points there instead. Nothing
                        to open when the slot isn't booked yet. */}
                    {slot.candidate ? (
                      <Link
                        to={`/roles/${slot.roleId}/candidates/${slot.candidate.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={iconBtnCls}
                      >
                        <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-entntblue" />
                      </Link>
                    ) : (
                      <span className={`${iconBtnCls} opacity-40 cursor-not-allowed`}>
                        <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-gray-400" />
                      </span>
                    )}
                    <button
                      type="button"
                      disabled={!!slot.candidateId}
                      onClick={() => handleEditSlot(slot)}
                      className={`${iconBtnCls} disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                      <PencilSquareIcon className="h-4 w-4 text-entntblue" />
                    </button>
                    <button
                      type="button"
                      disabled={!!slot.candidateId}
                      onClick={() => handleDeleteSlot(slot)}
                      className={`${iconBtnCls} disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                      <TrashIcon className="h-4 w-4 text-red-600" />
                    </button>
                    <button
                      type="button"
                      disabled={!slot.candidateId}
                      onClick={() => handleUnbookSlotClick(slot)}
                      title="Unbook this slot and notify the candidate by email"
                      className={`${iconBtnCls} disabled:cursor-not-allowed disabled:opacity-40`}
                    >
                      <NoSymbolIcon className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {selectedSlot && showModal && (
          <ConfirmModal
            type="danger"
            title="Delete Slot"
            open={showModal}
            setOpen={setShowModal}
            description={
              selectedSlot?.candidate ? (
                <span>
                  Are you sure you want to delete the slot for{" "}
                  <strong>{selectedSlot?.role.title + " "}</strong>
                  assigned to{" "}
                  <strong>
                    {" "}
                    {selectedSlot?.candidate.firstName +
                      " " +
                      selectedSlot?.candidate.lastName}
                  </strong>
                  ?
                </span>
              ) : (
                <span>
                  Are you sure you want to delete the slot for{" "}
                  <strong>{selectedSlot?.role.title}</strong>?
                </span>
              )
            }
            onConfirm={() => {
              handleDeleteConfirm(selectedSlot.id);
            }}
          />
        )}
        {showUnbookModal && slotsToUnbook.length > 0 && (
          <ConfirmModal
            type="danger"
            title={slotsToUnbook.length === 1 ? "Unbook Slot" : "Unbook Slots"}
            confirmButtonTitle="Unbook"
            open={showUnbookModal}
            setOpen={setShowUnbookModal}
            onCancel={() => setSlotsToUnbook([])}
            description={
              <div>
                <p className="mb-2">
                  Are you sure you want to unbook{" "}
                  {slotsToUnbook.length === 1 ? (
                    <>
                      the slot assigned to{" "}
                      <strong>
                        {slotsToUnbook[0].candidate?.firstName} {slotsToUnbook[0].candidate?.lastName}
                      </strong>
                      ?
                    </>
                  ) : (
                    <strong>{slotsToUnbook.length} slots</strong>
                  )}
                </p>
                <p className="text-xs text-gray-400">
                  The candidate{slotsToUnbook.length === 1 ? "" : "s"} will receive a cancellation email.
                </p>
              </div>
            }
            onConfirm={handleUnbookConfirm}
          />
        )}
        {showBulkDeleteModal && slotsToDelete.length > 0 && (
          <ConfirmModal
            type="danger"
            title={slotsToDelete.length === 1 ? "Delete Slot" : "Delete Slots"}
            confirmButtonTitle="Delete"
            open={showBulkDeleteModal}
            setOpen={setShowBulkDeleteModal}
            onCancel={() => setSlotsToDelete([])}
            description={
              <span>
                Are you sure you want to delete{" "}
                {slotsToDelete.length === 1 ? (
                  <>the slot for <strong>{slotsToDelete[0].role.title}</strong></>
                ) : (
                  <strong>{slotsToDelete.length} slots</strong>
                )}
                ?
              </span>
            }
            onConfirm={handleBulkDeleteConfirm}
          />
        )}
        {showEditModal && selectedSlot && (
          <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-50 p-4">
            <div
              className="bg-white dark:bg-gray-950 p-6 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 w-full max-w-lg"
              ref={editSlotRef}
            >
              <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-gray-100">
                Edit Interview Slot
              </h3>
              {error && (
                <div className="flex items-center gap-2 px-3 py-2.5 mb-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg">
                  <span className="text-red-600 dark:text-red-400 text-xs font-medium">{error}</span>
                </div>
              )}

              <div className="mb-4">
                <label className={labelCls}>
                  Start Time <span className="text-[#0d42ec] normal-case tracking-normal font-normal">*</span>
                </label>
                <div className="relative">
                  <ReactDatePicker
                    selected={startTime}
                    onChange={(date: Date | null) => setStartTime(date)}
                    showTimeSelect
                    minDate={new Date()}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    popperProps={{ strategy: "fixed" }}
                    popperClassName="!z-[9999]"
                    className={`${inputCls} w-full pl-10`}
                  />
                  <CalendarDaysIcon className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-gray-400" />
                </div>
              </div>
              <div className="mb-4">
                <label className={labelCls}>
                  End Time <span className="text-[#0d42ec] normal-case tracking-normal font-normal">*</span>
                </label>
                <div className="relative">
                  <ReactDatePicker
                    selected={endTime}
                    onChange={(date: Date | null) => setEndTime(date)}
                    showTimeSelect
                    minDate={new Date()}
                    dateFormat="MMMM d, yyyy h:mm aa"
                    popperProps={{ strategy: "fixed" }}
                    popperClassName="!z-[9999]"
                    className={`${inputCls} w-full pl-10`}
                  />
                  <CalendarDaysIcon className="w-4 h-4 absolute top-1/2 -translate-y-1/2 left-3 text-gray-400" />
                </div>
              </div>

              <label className={labelCls}>
                Meeting Link <span className="text-[#0d42ec] normal-case tracking-normal font-normal">*</span>
              </label>
              <input
                type="text"
                value={meetingLink || ""}
                onChange={(e) => setMeetingLink(e.target.value)}
                className={`${inputCls} w-full mb-4`}
              />
              <label className={labelCls}>Interviewers</label>

              {/* Selected chips */}
              {selectedInterviewers.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedInterviewers.map((person) => (
                    <div
                      key={person.id}
                      className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-full"
                    >
                      <div className="w-5 h-5 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                        {person.displayName.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        {person.displayName}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeInterviewer(person.id)}
                        className="text-blue-400 hover:text-red-500 transition-colors ml-0.5"
                        aria-label={`Remove ${person.displayName}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Search input with dropdown */}
              <div className="relative mb-4">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={interviewerSearch}
                    onChange={(e) => setInterviewerSearch(e.target.value)}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                    className="w-full pl-9 pr-9 py-2 rounded-md border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-darkbglight dark:text-gray-200 text-sm"
                    placeholder="Type a name to search (IT / Technology / Shipping)..."
                  />
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="animate-spin h-4 w-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    </div>
                  )}
                </div>

                {showDropdown && (
                  <div
                    className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden"
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {searchResults.filter((r) => !selectedInterviewers.some((s) => s.id === r.id)).length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">No matching users found</div>
                    ) : (
                      <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                        {searchResults
                          .filter((r) => !selectedInterviewers.some((s) => s.id === r.id))
                          .map((user) => (
                            <li
                              key={user.id}
                              onMouseDown={() => addInterviewer(user)}
                              className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors"
                            >
                              <div className="w-7 h-7 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                                {user.displayName?.charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{user.displayName}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.mail || user.userPrincipalName}</p>
                              </div>
                              {user.department && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full flex-shrink-0">
                                  {user.department}
                                </span>
                              )}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2.5 mt-5">
                <button
                  type="button"
                  onClick={handleCloseEditModal}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleEditSubmit}
                  className="px-5 py-2 text-sm font-semibold text-white bg-[#0d42ec] hover:bg-[#0b38cc] rounded-lg transition-colors shadow-sm"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewSlotShow;
